import {
  Button,
  Card,
  CardContent,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
  cn,
} from '@up2stay/up2promo-ui';
import {
  ProductScanSource,
  ProfileViewSource,
  SentimentTrendGranularity,
} from '@up2stay/up2promo-api-client';
import { ArrowRight, Info } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';

import { formatTrendLabel } from '@/core/components/reputation/reputation-bits';
import { shellPaths } from '@/core/router/app-paths';

import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardMetaClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';
import {
  averageScansPerDay,
  overlayPreviousPeriodScans,
  strongestActivityDay,
} from './dashboard-status-helpers';
import type {
  DashboardPeriodChange,
  DashboardProfileViewMetrics,
  DashboardRangeDays,
  DashboardScanPoint,
  DashboardScanSourceCount,
} from './dashboard-status-types';

const SCAN_SOURCE_ORDER = [
  ProductScanSource.QR,
  ProductScanSource.NFC,
  ProductScanSource.UNKNOWN,
] as const;

const PROFILE_VIEW_SOURCE_ORDER = [
  ProfileViewSource.WALLET,
  ProfileViewSource.PROFILE_QR,
  ProfileViewSource.DIRECT,
  ProfileViewSource.PRODUCT,
] as const;

const SCAN_SOURCE_FILL: Record<ProductScanSource, string> = {
  [ProductScanSource.QR]: 'var(--primary)',
  [ProductScanSource.NFC]:
    'color-mix(in oklab, var(--primary) 42%, var(--card))',
  [ProductScanSource.UNKNOWN]:
    'color-mix(in oklab, var(--muted-foreground) 40%, var(--card))',
};

const PROFILE_VIEW_SOURCE_FILL: Record<
  (typeof PROFILE_VIEW_SOURCE_ORDER)[number],
  string
> = {
  [ProfileViewSource.WALLET]:
    'color-mix(in oklab, var(--profile-series) 100%, transparent)',
  [ProfileViewSource.PROFILE_QR]:
    'color-mix(in oklab, var(--profile-series) 68%, transparent)',
  [ProfileViewSource.DIRECT]:
    'color-mix(in oklab, var(--profile-series) 42%, transparent)',
  [ProfileViewSource.PRODUCT]:
    'color-mix(in oklab, var(--profile-series) 22%, transparent)',
};

function periodChangeToneClass(
  direction: DashboardPeriodChange['direction'] | undefined,
): string {
  if (direction === 'up') return 'text-[color:var(--ok-fg)]';
  if (direction === 'down') return 'text-destructive';
  return 'text-muted-foreground';
}

/**
 * Spread aggregate source shares across each day’s total so stacked bars
 * match the Status Board mockup without a per-day source series yet.
 */
function stackDayBySources(
  scans: number,
  sources: DashboardScanSourceCount[],
  totalScans: number,
): Record<(typeof SCAN_SOURCE_ORDER)[number], number> {
  const empty = {
    [ProductScanSource.QR]: 0,
    [ProductScanSource.NFC]: 0,
    [ProductScanSource.UNKNOWN]: 0,
  };
  if (scans <= 0 || totalScans <= 0) return empty;
  const counts = new Map(sources.map((row) => [row.source, row.count]));
  const qrShare = (counts.get(ProductScanSource.QR) ?? 0) / totalScans;
  const nfcShare = (counts.get(ProductScanSource.NFC) ?? 0) / totalScans;
  const qr = Math.round(scans * qrShare);
  const nfc = Math.round(scans * nfcShare);
  const unknown = Math.max(0, scans - qr - nfc);
  return {
    [ProductScanSource.QR]: qr,
    [ProductScanSource.NFC]: nfc,
    [ProductScanSource.UNKNOWN]: unknown,
  };
}

export function DashboardTapsScansCard({
  scanSeries,
  previousScanSeries,
  totalScans,
  previousPeriodScans,
  scanSources,
  rangeDays,
  periodChange,
  periodChangeLabel,
  activeProductsCount,
  profileViews,
  canReadProfiles,
  canManageProducts,
  profileViewsPeriodChangeLabel,
  isLoading,
  locale,
  isMobile,
  onOpen,
  labels,
}: {
  scanSeries: DashboardScanPoint[];
  previousScanSeries: DashboardScanPoint[];
  totalScans: number;
  previousPeriodScans: number;
  scanSources: DashboardScanSourceCount[];
  rangeDays: DashboardRangeDays;
  periodChange?: DashboardPeriodChange;
  periodChangeLabel?: string;
  activeProductsCount: number;
  profileViews: DashboardProfileViewMetrics;
  canReadProfiles: boolean;
  canManageProducts: boolean;
  profileViewsPeriodChangeLabel?: string;
  isLoading: boolean;
  locale: string;
  isMobile: boolean;
  onOpen: (href: string) => void;
  labels: {
    title: string;
    subtitle: string;
    subtitleDesktop: string;
    empty: string;
    emptyDesc: string;
    emptyCta: string;
    productTaps: string;
    strongestDay: string;
    strongestDayValue: string;
    strongestDayValueDesktop: string;
    strongestDayTaps: string;
    strongestDayTapsOne: string;
    strongestDayViews: string;
    strongestDayViewsOne: string;
    avgPerDay: string;
    avgPerDayValue: string;
    avgViewsPerDay: string;
    avgViewsPerDayValue: string;
    activeProducts: string;
    activeProductsValue: string;
    legendPrevious: string;
    scanSourceQr: string;
    scanSourceNfc: string;
    scanSourceUnknown: string;
    profileViewsTitle: string;
    profileViewsEmpty: string;
    profileViewsEmptyDesc: string;
    profileViewsEmptyCta: string;
    profileViewsOpen: string;
    profileViewsSourceWallet: string;
    profileViewsSourceQr: string;
    profileViewsSourceDirect: string;
    profileViewsSourceProduct: string;
    profileViewsFootnote: string;
  };
}) {
  const scanChartConfig = useMemo(
    () =>
      ({
        [ProductScanSource.QR]: {
          label: labels.scanSourceQr,
          color: SCAN_SOURCE_FILL[ProductScanSource.QR],
        },
        [ProductScanSource.NFC]: {
          label: labels.scanSourceNfc,
          color: SCAN_SOURCE_FILL[ProductScanSource.NFC],
        },
        [ProductScanSource.UNKNOWN]: {
          label: labels.scanSourceUnknown,
          color: SCAN_SOURCE_FILL[ProductScanSource.UNKNOWN],
        },
        previousScans: {
          label: labels.legendPrevious,
          color: 'var(--muted-foreground)',
        },
      }) satisfies ChartConfig,
    [
      labels.legendPrevious,
      labels.scanSourceNfc,
      labels.scanSourceQr,
      labels.scanSourceUnknown,
    ],
  );

  const profileChartConfig = useMemo(
    () =>
      ({
        views: {
          label: labels.profileViewsTitle,
          color: 'var(--profile-series)',
        },
      }) satisfies ChartConfig,
    [labels.profileViewsTitle],
  );

  const scanChartData = useMemo(() => {
    const overlaid = overlayPreviousPeriodScans(scanSeries, previousScanSeries);
    return overlaid.map((point) => {
      const stack = stackDayBySources(point.scans, scanSources, totalScans);
      return {
        ...point,
        ...stack,
        label: formatTrendLabel(
          point.date,
          SentimentTrendGranularity.DAY,
          locale,
        ),
      };
    });
  }, [locale, previousScanSeries, scanSeries, scanSources, totalScans]);

  const profileChartData = useMemo(
    () =>
      profileViews.viewSeries.map((point) => ({
        ...point,
        label: formatTrendLabel(
          point.date,
          SentimentTrendGranularity.DAY,
          locale,
        ),
      })),
    [locale, profileViews.viewSeries],
  );

  const maxScans = Math.max(
    ...scanChartData.map((p) => Math.max(p.scans, p.previousScans)),
    1,
  );
  const maxViews = Math.max(...profileChartData.map((p) => p.views), 1);
  const hasScans = totalScans > 0;
  const hasPrevious =
    previousPeriodScans > 0 ||
    previousScanSeries.some((point) => point.scans > 0) ||
    scanChartData.some((point) => point.previousScans > 0);
  const strongest = strongestActivityDay(scanSeries, profileViews.viewSeries);
  const avgPerDay = averageScansPerDay(totalScans, rangeDays);
  const avgViewsPerDay = averageScansPerDay(profileViews.totalViews, rangeDays);

  const strongestActivity =
    strongest != null
      ? [
          strongest.scans > 0
            ? (strongest.scans === 1
                ? labels.strongestDayTapsOne
                : labels.strongestDayTaps
              ).replace('{count}', String(strongest.scans))
            : null,
          strongest.views > 0
            ? (strongest.views === 1
                ? labels.strongestDayViewsOne
                : labels.strongestDayViews
              ).replace('{count}', String(strongest.views))
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';
  const strongestLabel =
    strongest != null
      ? (isMobile ? labels.strongestDayValue : labels.strongestDayValueDesktop)
          .replace(
            '{date}',
            formatTrendLabel(
              strongest.date,
              SentimentTrendGranularity.DAY,
              locale,
            ),
          )
          .replace('{activity}', strongestActivity)
      : '—';

  const avgLabel = labels.avgPerDayValue.replace(
    '{avg}',
    avgPerDay.toLocaleString(locale, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }),
  );
  const avgViewsLabel = labels.avgViewsPerDayValue.replace(
    '{avg}',
    avgViewsPerDay.toLocaleString(locale, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }),
  );
  const activeProductsLabel = labels.activeProductsValue.replace(
    '{count}',
    String(activeProductsCount),
  );

  const scanSourceLabel: Record<ProductScanSource, string> = {
    [ProductScanSource.QR]: labels.scanSourceQr,
    [ProductScanSource.NFC]: labels.scanSourceNfc,
    [ProductScanSource.UNKNOWN]: labels.scanSourceUnknown,
  };
  const scanSourceCounts = new Map(
    scanSources.map((row) => [row.source, row.count]),
  );

  const profileViewSourceLabel: Record<
    (typeof PROFILE_VIEW_SOURCE_ORDER)[number],
    string
  > = {
    [ProfileViewSource.WALLET]: labels.profileViewsSourceWallet,
    [ProfileViewSource.PROFILE_QR]: labels.profileViewsSourceQr,
    [ProfileViewSource.DIRECT]: labels.profileViewsSourceDirect,
    [ProfileViewSource.PRODUCT]: labels.profileViewsSourceProduct,
  };
  const profileViewSourceCounts = new Map(
    profileViews.sources.map((row) => [row.source, row.count]),
  );
  const hasProfileViews = profileViews.totalViews > 0;
  const profileViewsFootnote = labels.profileViewsFootnote.replace(
    '{count}',
    profileViews.viaProductCount.toLocaleString(locale),
  );

  if (isLoading) {
    return (
      <Card className={cn(statusBoardCardClass, 'h-full')} aria-busy="true">
        <CardContent className={cn('space-y-4', statusBoardCardPadClass)}>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-44 w-full rounded-md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <section
      aria-labelledby="dashboard-taps-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn(
            'flex h-full flex-col gap-[18px]',
            statusBoardCardPadClass,
          )}
        >
          <div className="min-w-0">
            <h2
              id="dashboard-taps-heading"
              className={statusBoardSectionTitleClass}
            >
              {labels.title}
            </h2>
            <p className={cn('mt-0.5 text-pretty', statusBoardMetaClass)}>
              {(isMobile ? labels.subtitle : labels.subtitleDesktop).replace(
                '{days}',
                String(rangeDays),
              )}
            </p>
          </div>

          {/* Produkt-Taps */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <div className="flex min-w-0 flex-wrap items-baseline gap-2.5">
                <span className="inline-flex items-center gap-1.5 self-center text-[13px] font-semibold text-foreground">
                  <span
                    className="size-2 shrink-0 rounded-[2px] bg-[color:var(--primary)]"
                    aria-hidden
                  />
                  {labels.productTaps}
                </span>
                <span className="text-[26px] font-bold leading-none tracking-tight text-foreground">
                  {totalScans.toLocaleString(locale)}
                </span>
                {periodChangeLabel ? (
                  <span
                    className={cn(
                      'text-[12px] font-medium tabular-nums',
                      periodChangeToneClass(periodChange?.direction),
                    )}
                  >
                    {periodChangeLabel}
                  </span>
                ) : null}
              </div>
              {hasScans ? (
                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12px] text-muted-foreground">
                  {SCAN_SOURCE_ORDER.map((source) => (
                    <span
                      key={source}
                      className="inline-flex items-center gap-1.5"
                    >
                      <span
                        className="size-2 shrink-0 rounded-[2px]"
                        style={{ background: SCAN_SOURCE_FILL[source] }}
                        aria-hidden
                      />
                      {scanSourceLabel[source]}
                      <b className="font-mono font-semibold text-foreground">
                        {(scanSourceCounts.get(source) ?? 0).toLocaleString(
                          locale,
                        )}
                      </b>
                    </span>
                  ))}
                  {hasPrevious ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="w-3 border-t-[1.5px] border-dashed border-muted-foreground"
                        aria-hidden
                      />
                      {labels.legendPrevious}
                      <b className="font-mono font-semibold text-foreground">
                        {previousPeriodScans.toLocaleString(locale)}
                      </b>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {hasScans ? (
              <ChartContainer
                config={scanChartConfig}
                className={cn(
                  'aspect-auto w-full',
                  isMobile ? 'h-[9.5rem]' : 'h-[150px]',
                )}
              >
                <ComposedChart
                  accessibilityLayer
                  data={scanChartData}
                  margin={{ left: 0, right: 4, top: 8, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={false}
                    strokeDasharray="2 6"
                    className="stroke-border/35"
                  />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={isMobile ? 36 : 28}
                    interval="preserveStartEnd"
                    className="text-[11px]"
                  />
                  <YAxis hide domain={[0, Math.ceil(maxScans * 1.12)]} />
                  <ChartTooltip
                    cursor={{
                      fill: 'color-mix(in oklab, var(--primary) 8%, transparent)',
                    }}
                    content={(tooltipProps) => (
                      <ChartTooltipContent
                        {...(tooltipProps as ComponentProps<
                          typeof ChartTooltipContent
                        >)}
                        indicator="line"
                        labelKey="label"
                      />
                    )}
                  />
                  {SCAN_SOURCE_ORDER.map((source, index) => (
                    <Bar
                      key={source}
                      dataKey={source as string}
                      name={scanSourceLabel[source]}
                      stackId="scans"
                      fill={SCAN_SOURCE_FILL[source]}
                      radius={
                        index === SCAN_SOURCE_ORDER.length - 1
                          ? [3, 3, 0, 0]
                          : [0, 0, 0, 0]
                      }
                      maxBarSize={isMobile ? 10 : 14}
                    />
                  ))}
                  {hasPrevious ? (
                    <Line
                      type="monotone"
                      dataKey="previousScans"
                      name={labels.legendPrevious}
                      stroke="var(--muted-foreground)"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      strokeOpacity={0.75}
                      dot={false}
                      activeDot={false}
                      isAnimationActive={!isMobile}
                    />
                  ) : null}
                </ComposedChart>
              </ChartContainer>
            ) : (
              <div className="flex flex-col items-start gap-2 rounded-[14px] border border-dashed border-border/70 bg-muted/15 px-4 py-5">
                <p className="text-[13px] font-medium text-foreground">
                  {labels.empty}
                </p>
                <p className="text-[12px] leading-snug text-muted-foreground">
                  {labels.emptyDesc}
                </p>
                {canManageProducts ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-auto min-h-0 px-0 text-[12px] font-semibold text-[color:var(--ghost-accent-fg)] hover:bg-transparent hover:text-[color:var(--ghost-accent-fg)]"
                    // Same target as the header „Produkt einrichten“ (DEV-542).
                    onClick={() => onOpen(shellPaths.moreProductsSetup)}
                  >
                    {labels.emptyCta}
                    <ArrowRight className="ml-1 size-3.5" />
                  </Button>
                ) : null}
              </div>
            )}
          </div>

          {/* Profilaufrufe */}
          {canReadProfiles ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-wrap items-baseline gap-2.5">
                  <span className="inline-flex items-center gap-1.5 self-center text-[13px] font-semibold text-foreground">
                    <span
                      className="size-2 shrink-0 rounded-full bg-[color:var(--profile-series)]"
                      aria-hidden
                    />
                    {labels.profileViewsTitle}
                  </span>
                  {hasProfileViews ? (
                    <>
                      <span className="text-[26px] font-bold leading-none tracking-tight text-foreground">
                        {profileViews.totalViews.toLocaleString(locale)}
                      </span>
                      {profileViewsPeriodChangeLabel ? (
                        <span
                          className={cn(
                            'text-[12px] font-medium tabular-nums',
                            periodChangeToneClass(
                              profileViews.periodChange.direction,
                            ),
                          )}
                        >
                          {profileViewsPeriodChangeLabel}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </div>
                {hasProfileViews ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div className="flex min-w-[10rem] flex-col gap-1.5">
                      <div className="flex h-1.5 w-full overflow-hidden rounded-[3px]">
                        {PROFILE_VIEW_SOURCE_ORDER.map((source) => {
                          const count =
                            profileViewSourceCounts.get(source) ?? 0;
                          if (count <= 0) return null;
                          return (
                            <span
                              key={source}
                              className="min-w-0"
                              style={{
                                flex: `${count} 1 0`,
                                background: PROFILE_VIEW_SOURCE_FILL[source],
                              }}
                            />
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                        {PROFILE_VIEW_SOURCE_ORDER.map((source) => (
                          <span
                            key={source}
                            className="inline-flex items-center gap-1.5"
                          >
                            <span
                              className="size-2 shrink-0 rounded-full"
                              style={{
                                background: PROFILE_VIEW_SOURCE_FILL[source],
                              }}
                              aria-hidden
                            />
                            {profileViewSourceLabel[source]}
                            <b className="font-mono font-semibold text-foreground">
                              {(
                                profileViewSourceCounts.get(source) ?? 0
                              ).toLocaleString(locale)}
                            </b>
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-[color:var(--ghost-accent-fg)]"
                      onClick={() => onOpen(shellPaths.profiles)}
                    >
                      {labels.profileViewsOpen}
                      <ArrowRight className="size-3.5" />
                    </button>
                  </div>
                ) : null}
              </div>

              {hasProfileViews ? (
                <ChartContainer
                  config={profileChartConfig}
                  className="aspect-auto h-[60px] w-full"
                >
                  <ComposedChart
                    accessibilityLayer
                    data={profileChartData}
                    margin={{ left: 0, right: 4, top: 4, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="dashboardProfileViewsFill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="var(--profile-series)"
                          stopOpacity={0.22}
                        />
                        <stop
                          offset="100%"
                          stopColor="var(--profile-series)"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={6}
                      minTickGap={isMobile ? 40 : 32}
                      interval="preserveStartEnd"
                      className="text-[11px]"
                    />
                    <YAxis hide domain={[0, Math.ceil(maxViews * 1.15)]} />
                    <ChartTooltip
                      cursor={false}
                      content={(tooltipProps) => (
                        <ChartTooltipContent
                          {...(tooltipProps as ComponentProps<
                            typeof ChartTooltipContent
                          >)}
                          indicator="line"
                          labelKey="label"
                        />
                      )}
                    />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name={labels.profileViewsTitle}
                      stroke="var(--profile-series)"
                      strokeWidth={2}
                      fill="url(#dashboardProfileViewsFill)"
                      isAnimationActive={!isMobile}
                    />
                  </ComposedChart>
                </ChartContainer>
              ) : (
                <div className="flex flex-col items-start gap-2 rounded-[14px] border border-dashed border-border/70 bg-muted/15 px-4 py-5">
                  <p className="text-[13px] font-medium text-foreground">
                    {labels.profileViewsEmpty}
                  </p>
                  <p className="text-[12px] leading-snug text-muted-foreground">
                    {labels.profileViewsEmptyDesc}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-auto min-h-0 px-0 text-[12px] font-semibold text-[color:var(--ghost-accent-fg)] hover:bg-transparent hover:text-[color:var(--ghost-accent-fg)]"
                    onClick={() => onOpen(shellPaths.profiles)}
                  >
                    {labels.profileViewsEmptyCta}
                    <ArrowRight className="ml-1 size-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ) : null}

          <div className="mt-auto grid grid-cols-2 gap-4 border-t border-border/50 pt-4 md:grid-cols-4">
            <div className="min-w-0">
              <div className="text-[11.5px] text-muted-foreground">
                {labels.strongestDay}
              </div>
              <div className="mt-0.5 text-[13px] font-semibold text-foreground">
                {strongestLabel}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11.5px] text-muted-foreground">
                {labels.avgPerDay}
              </div>
              <div className="mt-0.5 text-[13px] font-semibold text-foreground">
                {avgLabel}
              </div>
            </div>
            {canReadProfiles ? (
              <div className="min-w-0">
                <div className="text-[11.5px] text-muted-foreground">
                  {labels.avgViewsPerDay}
                </div>
                <div className="mt-0.5 text-[13px] font-semibold text-foreground">
                  {avgViewsLabel}
                </div>
              </div>
            ) : null}
            <div className="min-w-0">
              <div className="text-[11.5px] text-muted-foreground">
                {labels.activeProducts}
              </div>
              <div className="mt-0.5 text-[13px] font-semibold text-foreground">
                {activeProductsLabel}
              </div>
            </div>
          </div>

          {canReadProfiles && profileViews.viaProductCount > 0 ? (
            <div className="flex items-start gap-1.5 text-[11.5px] leading-snug text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span className="text-pretty">{profileViewsFootnote}</span>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}
